import { useState, useEffect, useRef } from 'react';
import { writeAuditLog } from '@/lib/auditLog';
import { Settings as SettingsIcon, Bell, User, Edit2, Save, X, Trash2, Shield, Camera, Upload, Bot, Smartphone, Mail, Play, Pause, Clock, Zap, RefreshCw, TrendingUp, FileText, Eye, Sliders } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { notificationPreferencesSchema } from '@/lib/validation';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { SEO } from '@/components/SEO';
import { useNavigate } from 'react-router-dom';
import { UsageDashboard } from '@/components/UsageDashboard';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { SubscriptionManagement } from '@/components/SubscriptionManagement';
import { MobileDrawer, MobileDrawerTrigger, MobileDrawerContent, MobileDrawerHeader, MobileDrawerTitle, MobileDrawerOption } from '@/components/ui/mobile-drawer';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { PushNotificationToggle } from '@/components/PushNotificationToggle';

export default function Settings() {
  const { user, profile } = useAuth();
  const { tier, nextTier, nextTierPrice } = usePlanLimits();
  const { toast: shadToast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    high_risk_invoices: true,
    reconciliation_matches: true
  });
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: profile?.name || '',
    email: user?.email || '',
    avatar_url: profile?.avatar_url || ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Autopilot Settings
  const [autopilotSettings, setAutopilotSettings] = useState({
    autonomyMode: 'supervised' as 'supervised' | 'autopilot' | 'full_auto',
    autoMatchThreshold: 85,
    reviewThreshold: 70,
    autoApproveRiskThreshold: 30,
    digestFrequency: 'daily' as 'realtime' | 'daily' | 'weekly',
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
  });
  const [savingAutopilot, setSavingAutopilot] = useState(false);
  const [digestDrawerOpen, setDigestDrawerOpen] = useState(false);
  const [modeDrawerOpen, setModeDrawerOpen] = useState(false);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      // Fetch notification preferences
      const { data: notifData } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (notifData) {
        setPreferences(notifData);
      } else {
        await supabase.from('notification_preferences').insert({
          user_id: user.id,
          ...preferences
        });
      }

      // Fetch user settings (autopilot)
      const { data: settingsData } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (settingsData) {
        setAutopilotSettings({
          autonomyMode: (settingsData.autonomy_mode as any) || 'supervised',
          autoMatchThreshold: settingsData.auto_match_threshold || 85,
          reviewThreshold: settingsData.review_threshold || 70,
          autoApproveRiskThreshold: settingsData.auto_approve_risk_threshold || 30,
          digestFrequency: (settingsData.digest_frequency as any) || 'daily',
          quietHoursStart: settingsData.quiet_hours_start || '22:00',
          quietHoursEnd: settingsData.quiet_hours_end || '08:00',
        });
      }
    };

    setProfileData({
      name: profile?.name || '',
      email: user?.email || '',
      avatar_url: profile?.avatar_url || ''
    });

    fetchData();
  }, [user, profile]);

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('image/')) {
      shadToast({ variant: 'destructive', title: 'Invalid File', description: 'Please upload an image file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      shadToast({ variant: 'destructive', title: 'File Too Large', description: 'Image must be less than 5MB' });
      return;
    }

    setUploadingAvatar(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      const avatarUrl = `${publicUrl}?t=${Date.now()}`;
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfileData(prev => ({ ...prev, avatar_url: avatarUrl }));
      toast.success('Avatar updated');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSavePreferences = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const result = notificationPreferencesSchema.safeParse(preferences);
      
      if (!result.success) {
        shadToast({ variant: 'destructive', title: 'Validation Failed', description: result.error.issues[0]?.message || 'Invalid preferences' });
        setLoading(false);
        return;
      }

      const { error } = await supabase
        .from('notification_preferences')
        .upsert({ user_id: user.id, ...result.data });

      if (error) throw error;
      toast.success('Preferences saved');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!user) return;

    setLoading(true);
    try {
      if (!profileData.name.trim()) {
        shadToast({ variant: 'destructive', title: 'Validation Error', description: 'Name cannot be empty' });
        setLoading(false);
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ name: profileData.name.trim() })
        .eq('id', user.id);

      if (error) throw error;
      writeAuditLog({ action: 'profile.updated', resource_type: 'profile', details: { name: profileData.name.trim() } });
      toast.success('Profile updated');
      setEditingProfile(false);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;

    setLoading(true);
    try {
      if (passwordData.newPassword.length < 8) {
        shadToast({ variant: 'destructive', title: 'Validation Error', description: 'Password must be at least 8 characters' });
        setLoading(false);
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        shadToast({ variant: 'destructive', title: 'Validation Error', description: 'Passwords do not match' });
        setLoading(false);
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: passwordData.newPassword });
      if (error) throw error;

      writeAuditLog({ action: 'password.changed', resource_type: 'profile' });
      toast.success('Password changed');
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setChangingPassword(false);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAutopilotSettings = async () => {
    if (!user) return;

    setSavingAutopilot(true);
    try {
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          autonomy_mode: autopilotSettings.autonomyMode,
          auto_match_threshold: autopilotSettings.autoMatchThreshold,
          review_threshold: autopilotSettings.reviewThreshold,
          auto_approve_risk_threshold: autopilotSettings.autoApproveRiskThreshold,
          digest_frequency: autopilotSettings.digestFrequency,
          quiet_hours_start: autopilotSettings.quietHoursStart,
          quiet_hours_end: autopilotSettings.quietHoursEnd,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      toast.success('Autopilot settings saved');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSavingAutopilot(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const now = new Date().toISOString();
      writeAuditLog({ action: 'account.deleted', resource_type: 'profile', details: { email: user.email, soft_delete: true } });

      // Soft delete: set deleted_at instead of hard delete for data retention compliance
      await Promise.all([
        supabase.from('bank_transactions').update({ deleted_at: now } as any).eq('user_id', user.id),
        supabase.from('tally_entries').update({ deleted_at: now } as any).eq('user_id', user.id),
        supabase.from('invoices').update({ deleted_at: now } as any).eq('user_id', user.id),
        supabase.from('profiles').update({ deleted_at: now } as any).eq('id', user.id),
        // Hard delete non-critical operational data
        supabase.from('notification_preferences').delete().eq('user_id', user.id),
        supabase.from('logs').delete().eq('user_id', user.id),
      ]);

      await supabase.auth.signOut();
      toast.success('Account deleted');
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const autonomyModes = [
    { id: 'supervised', label: 'Supervised', description: 'Review all actions before execution', icon: Eye },
    { id: 'autopilot', label: 'Autopilot', description: 'Auto-execute low-risk, review high-risk', icon: Zap },
    { id: 'full_auto', label: 'Full Auto', description: 'Execute all actions automatically', icon: Bot },
  ];

  const digestOptions = [
    { id: 'realtime', label: 'Real-time', description: 'Instant notifications' },
    { id: 'daily', label: 'Daily Digest', description: 'Once per day summary' },
    { id: 'weekly', label: 'Weekly Digest', description: 'Weekly summary' },
  ];

  return (
    <>
      <SEO title="Settings - Sentinel" description="Manage your profile, security, and agent settings" noindex={true} />
      
      <div className="min-h-full bg-background">
        <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 rounded-3xl bg-gradient-primary shadow-glow">
                <SettingsIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Settings</h1>
                <p className="text-sm text-muted-foreground">Manage your account and agent preferences</p>
              </div>
            </div>
          </motion.div>

          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 rounded-3xl p-1.5 h-auto">
              <TabsTrigger value="profile" className="rounded-2xl py-3">Profile</TabsTrigger>
              <TabsTrigger value="autopilot" className="rounded-2xl py-3">Autopilot</TabsTrigger>
              <TabsTrigger value="notifications" className="rounded-2xl py-3">Alerts</TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6">
              {/* Profile Card */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="rounded-3xl">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-3">
                        <User className="text-primary" size={22} />
                        Profile Information
                      </CardTitle>
                      {!editingProfile && (
                        <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)} className="rounded-2xl">
                          <Edit2 size={16} className="mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="relative">
                        {profileData.avatar_url ? (
                          <img src={profileData.avatar_url} alt={profileData.name || 'User'} className="w-24 h-24 rounded-3xl object-cover border-2 border-border" />
                        ) : (
                          <div className="w-24 h-24 rounded-3xl bg-gradient-primary flex items-center justify-center shadow-glow">
                            <span className="text-white text-3xl font-bold">{profileData.name?.[0]?.toUpperCase() || 'U'}</span>
                          </div>
                        )}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingAvatar}
                          className="absolute -bottom-2 -right-2 w-10 h-10 rounded-2xl bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50 shadow-lg"
                        >
                          {uploadingAvatar ? <Upload size={16} className="animate-pulse" /> : <Camera size={16} />}
                        </button>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                      </div>
                      <div className="text-center sm:text-left">
                        <p className="font-medium text-foreground text-lg">{profileData.name || 'Not set'}</p>
                        <p className="text-sm text-muted-foreground">{profileData.email}</p>
                        <Badge variant="secondary" className="mt-2 rounded-xl capitalize">{profile?.subscription || 'Free'}</Badge>
                      </div>
                    </div>

                    {editingProfile && (
                      <div className="space-y-4 pt-4 border-t border-border">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name</Label>
                          <Input id="name" value={profileData.name} onChange={(e) => setProfileData({ ...profileData, name: e.target.value })} placeholder="Your name" className="rounded-2xl" />
                        </div>
                        <div className="flex gap-3">
                          <Button onClick={handleUpdateProfile} disabled={loading} className="rounded-2xl">
                            <Save size={16} className="mr-2" />
                            Save
                          </Button>
                          <Button variant="outline" onClick={() => setEditingProfile(false)} disabled={loading} className="rounded-2xl">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Usage Dashboard */}
              <UsageDashboard />

              {/* Subscription */}
              <SubscriptionManagement />

              {/* Security */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card className="rounded-3xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Shield className="text-primary" size={22} />
                      Security
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!changingPassword ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Password</Label>
                          <p className="text-sm text-muted-foreground mt-1">••••••••</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setChangingPassword(true)} className="rounded-2xl">
                          Change
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>New Password</Label>
                          <Input type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} placeholder="Enter new password" className="rounded-2xl" />
                        </div>
                        <div className="space-y-2">
                          <Label>Confirm Password</Label>
                          <Input type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} placeholder="Confirm password" className="rounded-2xl" />
                        </div>
                        <div className="flex gap-3">
                          <Button onClick={handleChangePassword} disabled={loading} className="rounded-2xl">Update Password</Button>
                          <Button variant="outline" onClick={() => { setChangingPassword(false); setPasswordData({ newPassword: '', confirmPassword: '' }); }} className="rounded-2xl">Cancel</Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Danger Zone */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <Card className="border-destructive/50 rounded-3xl">
                  <CardHeader>
                    <CardTitle className="text-destructive flex items-center gap-3">
                      <Trash2 size={22} />
                      Danger Zone
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">Once deleted, your account and all data will be permanently removed.</p>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" className="rounded-2xl">
                          <Trash2 size={16} className="mr-2" />
                          Delete Account
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-3xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete your account and all data.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleDeleteAccount} className="bg-destructive hover:bg-destructive/90 rounded-2xl">Delete Account</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardContent>
                </Card>
              </motion.div>
            </TabsContent>

            {/* Autopilot Tab */}
            <TabsContent value="autopilot" className="space-y-6">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="rounded-3xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Bot className="text-primary" size={22} />
                      Agent Mode
                    </CardTitle>
                    <CardDescription>Control how Sentinel handles tasks autonomously</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <MobileDrawer open={modeDrawerOpen} onOpenChange={setModeDrawerOpen}>
                      <MobileDrawerTrigger asChild>
                        <button className="w-full p-4 rounded-2xl border border-border bg-surface/50 text-left hover:bg-surface transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                                {autopilotSettings.autonomyMode === 'supervised' && <Eye className="h-6 w-6 text-primary" />}
                                {autopilotSettings.autonomyMode === 'autopilot' && <Zap className="h-6 w-6 text-primary" />}
                                {autopilotSettings.autonomyMode === 'full_auto' && <Bot className="h-6 w-6 text-primary" />}
                              </div>
                              <div>
                                <p className="font-medium text-foreground capitalize">{autopilotSettings.autonomyMode.replace('_', ' ')}</p>
                                <p className="text-sm text-muted-foreground">
                                  {autonomyModes.find(m => m.id === autopilotSettings.autonomyMode)?.description}
                                </p>
                              </div>
                            </div>
                            <Sliders className="h-5 w-5 text-muted-foreground" />
                          </div>
                        </button>
                      </MobileDrawerTrigger>
                      <MobileDrawerContent>
                        <MobileDrawerHeader>
                          <MobileDrawerTitle>Select Agent Mode</MobileDrawerTitle>
                        </MobileDrawerHeader>
                        <div className="p-4 space-y-2">
                          {autonomyModes.map((mode) => (
                            <MobileDrawerOption
                              key={mode.id}
                              label={mode.label}
                              description={mode.description}
                              selected={autopilotSettings.autonomyMode === mode.id}
                              onSelect={() => {
                                setAutopilotSettings(prev => ({ ...prev, autonomyMode: mode.id as any }));
                                setModeDrawerOpen(false);
                              }}
                              icon={<mode.icon className="h-5 w-5" />}
                            />
                          ))}
                        </div>
                      </MobileDrawerContent>
                    </MobileDrawer>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <Card className="rounded-3xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Sliders className="text-primary" size={22} />
                      Confidence Thresholds
                    </CardTitle>
                    <CardDescription>Fine-tune when Sentinel acts vs. asks for review</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-8">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label>Auto-match confidence</Label>
                        <Badge variant="secondary" className="rounded-xl font-mono">{autopilotSettings.autoMatchThreshold}%</Badge>
                      </div>
                      <Slider
                        value={[autopilotSettings.autoMatchThreshold]}
                        onValueChange={([value]) => setAutopilotSettings(prev => ({ ...prev, autoMatchThreshold: value }))}
                        min={50}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">Matches above this threshold are auto-confirmed</p>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label>Review threshold</Label>
                        <Badge variant="secondary" className="rounded-xl font-mono">{autopilotSettings.reviewThreshold}%</Badge>
                      </div>
                      <Slider
                        value={[autopilotSettings.reviewThreshold]}
                        onValueChange={([value]) => setAutopilotSettings(prev => ({ ...prev, reviewThreshold: value }))}
                        min={30}
                        max={90}
                        step={5}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">Items below this threshold are flagged for human review</p>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label>Auto-approve risk threshold</Label>
                        <Badge variant="secondary" className="rounded-xl font-mono">&lt;{autopilotSettings.autoApproveRiskThreshold}%</Badge>
                      </div>
                      <Slider
                        value={[autopilotSettings.autoApproveRiskThreshold]}
                        onValueChange={([value]) => setAutopilotSettings(prev => ({ ...prev, autoApproveRiskThreshold: value }))}
                        min={0}
                        max={50}
                        step={5}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">Invoices with risk below this are auto-approved</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card className="rounded-3xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Clock className="text-primary" size={22} />
                      Digest & Quiet Hours
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <MobileDrawer open={digestDrawerOpen} onOpenChange={setDigestDrawerOpen}>
                      <MobileDrawerTrigger asChild>
                        <button className="w-full p-4 rounded-2xl border border-border bg-surface/50 text-left hover:bg-surface transition-colors">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-foreground capitalize">{autopilotSettings.digestFrequency.replace('_', ' ')}</p>
                              <p className="text-sm text-muted-foreground">{digestOptions.find(d => d.id === autopilotSettings.digestFrequency)?.description}</p>
                            </div>
                            <Sliders className="h-5 w-5 text-muted-foreground" />
                          </div>
                        </button>
                      </MobileDrawerTrigger>
                      <MobileDrawerContent>
                        <MobileDrawerHeader>
                          <MobileDrawerTitle>Notification Frequency</MobileDrawerTitle>
                        </MobileDrawerHeader>
                        <div className="p-4 space-y-2">
                          {digestOptions.map((option) => (
                            <MobileDrawerOption
                              key={option.id}
                              label={option.label}
                              description={option.description}
                              selected={autopilotSettings.digestFrequency === option.id}
                              onSelect={() => {
                                setAutopilotSettings(prev => ({ ...prev, digestFrequency: option.id as any }));
                                setDigestDrawerOpen(false);
                              }}
                            />
                          ))}
                        </div>
                      </MobileDrawerContent>
                    </MobileDrawer>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Quiet hours start</Label>
                        <Input
                          type="time"
                          value={autopilotSettings.quietHoursStart}
                          onChange={(e) => setAutopilotSettings(prev => ({ ...prev, quietHoursStart: e.target.value }))}
                          className="rounded-2xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Quiet hours end</Label>
                        <Input
                          type="time"
                          value={autopilotSettings.quietHoursEnd}
                          onChange={(e) => setAutopilotSettings(prev => ({ ...prev, quietHoursEnd: e.target.value }))}
                          className="rounded-2xl"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Non-urgent notifications are held during quiet hours</p>
                  </CardContent>
                </Card>
              </motion.div>

              <Button onClick={handleSaveAutopilotSettings} disabled={savingAutopilot} className="w-full rounded-2xl h-12">
                {savingAutopilot ? 'Saving...' : 'Save Autopilot Settings'}
              </Button>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="space-y-6">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="rounded-3xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Bell className="text-primary" size={22} />
                      Alert Preferences
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Push Notifications Section */}
                    <div className="flex items-center justify-between p-4 rounded-2xl bg-surface/50">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                          <Smartphone className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <Label className="text-base">Push Notifications</Label>
                          <p className="text-sm text-muted-foreground">Daily smart alerts on your device</p>
                        </div>
                      </div>
                      <PushNotificationToggle />
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-2xl bg-surface/50">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                          <Mail className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <Label className="text-base">Email Notifications</Label>
                          <p className="text-sm text-muted-foreground">Receive email alerts for important events</p>
                        </div>
                      </div>
                      <Switch checked={preferences.email_enabled} onCheckedChange={(checked) => setPreferences({ ...preferences, email_enabled: checked })} />
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-2xl bg-surface/50">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-destructive/10 flex items-center justify-center">
                          <FileText className="h-6 w-6 text-destructive" />
                        </div>
                        <div>
                          <Label className="text-base">Flagged Invoice Alerts</Label>
                          <p className="text-sm text-muted-foreground">Get notified when duplicates are detected</p>
                        </div>
                      </div>
                      <Switch checked={preferences.high_risk_invoices} onCheckedChange={(checked) => setPreferences({ ...preferences, high_risk_invoices: checked })} />
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-2xl bg-success/10 border border-success/20">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-success/20 flex items-center justify-center">
                          <RefreshCw className="h-6 w-6 text-success" />
                        </div>
                        <div>
                          <Label className="text-base">Reconciliation Matches</Label>
                          <p className="text-sm text-muted-foreground">Notify when new matches are found</p>
                        </div>
                      </div>
                      <Switch checked={preferences.reconciliation_matches} onCheckedChange={(checked) => setPreferences({ ...preferences, reconciliation_matches: checked })} />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <Button onClick={handleSavePreferences} disabled={loading} className="w-full rounded-2xl h-12">
                {loading ? 'Saving...' : 'Save Preferences'}
              </Button>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}
